import selectn from 'selectn'
import { memoizr } from '../../../reduxion-core/lib'
import actionTypes from '../../constants/actionTypes'
import * as transform from '../../lib/transform'
import { getHost, getQueryString, getGameSummary, getLeagues, apiReqStart, apiReqComplete, getStandings as getStandingsCommon } from './index'
import { getErrorRedirect } from '../../actions/utils'
import qs from 'query-string'

/**
 * get the pre-rendered Preview/Report HTML for injectino into Preview / Report tab
 * @param  {Object} edition     edition config
 * @param  {Object} params      router params
 * @param  {Object} location    router location
 * @param  {Boolean} isPolling  isPolling
 * @return {Function}           handled by thunk middleware
 */
export function getArticleTab({ edition = {}, params = {}, location = {}, isPolling, appProps }) {
  return (dispatch, getState) => {
    return new Promise((resolve) => {
      const gamepackage = getGameSummary({ edition, params, location, isPolling, appProps })
      const { promise } = gamepackage

      dispatch(apiReqStart(isPolling))

      promise
        .then((res) => {

          dispatch({
            params,
            edition,
            location,
            url: gamepackage.url,
            type: gamepackage.type,
            data : gamepackage.transform({ res, edition, params, location, appProps })
          })

          const state = getState()
          const sCoreHost = getHost('score')
          const article = selectn('gamePackage.article', state) || {}
          const host = edition && edition.domain && 'https://'+edition.domain

          let articleId = article.id
          let articleType = article.type || ''
          let html = article.html || ''

          const requiredPageTypes = {
            Recap: 'report',
            Preview: 'preview'
          }

          if(params.pageType !== requiredPageTypes[articleType]) {
            articleId = undefined
          }

          if(params.day || !articleId) {
            const matchCoveragePreviewReport = [].concat.apply([], (selectn('gamePackage.matchCoverage', state) || []).map((a) => {
              return a.articles
            })).find(a => {
              return (requiredPageTypes[a.type] === params.pageType) && ((parseInt(params.day, 10) === parseInt(a.gameDay, 10)) || !articleId)
            })
            if(matchCoveragePreviewReport) {
              articleId = matchCoveragePreviewReport.id
              html = ''
            }
          }

          // If there is no article id or if html is already present,
          // resolve and return
          if(!articleId || html.length) {
            dispatch(apiReqComplete())
            resolve(false)
            return
          }

          const qsObj = {
            'edition-host': edition.host,
            render: true,
            partial: 'article',
            tabtype: params.pageType,
            xhr: 1,
            country: edition.userGeo
          }

          if (edition.isEspnCricinfo) {
            qsObj.site = edition.site
          }

          if(params.platform) {
            qsObj.platform = params.platform
          }
          if(appProps.siteType === 'data-lite') {
            qsObj.connectionspeed = appProps.siteType
          }

          const url = `${params.origin || host || sCoreHost}/story/_/id/${articleId}${getQueryString(qsObj, edition, params)}`

          const articlePromise = memoizr.get(url)

          articlePromise
            .then((article) => {
              dispatch(apiReqComplete())
              let htmlRaw = selectn('data.content.html', article)
              let type = selectn('data.content.type', article)
              dispatch({
                url,
                edition,
                location,
                params,
                data: {
                  article: {
                    id: articleId,
                    type: type,
                    html: htmlRaw.replace('</script>','<\\/script>')
                  }
                },
                type: actionTypes.GET_RENDERED_ARTICLE
              })
              resolve(true)
            })
        })
        .catch((e) => {
          dispatch(apiReqComplete())
          resolve(true)
          console.error(e)
        })
    })
  }
}

export function getCommentaryTab({ edition = {}, params = {}, location = {}, isPolling, appProps }) {
  return (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      // parse 'query' object from location.search when not available on server side
      if (typeof location.query === 'undefined') {
        location = Object.assign({}, location, {
          query : qs.parse(location.search)
        })
      }

      dispatch(apiReqStart(isPolling))

      const gamepackage = getGameSummary({ edition, params, location, isPolling, appProps })

      const { promise } = gamepackage

      promise
        .then((res) => {

          dispatch({
            params,
            edition,
            location,
            url: gamepackage.url,
            type: gamepackage.type,
            data : gamepackage.transform({ res, edition, params, location, appProps })
          })

          const state = getState()
          const filter = selectn('gamePackage.selCmtryFilter.playtype', state)
          const innings = selectn('gamePackage.selInnings.period', state)
          let commentaryArgs = { dispatch, state, edition, params, location, isPolling, resolve, appProps }
          const { query = {} } = location
          const queryModified = Object.assign({}, query, {
            innings,
            filter
          })

          const locationModified = Object.assign({}, location, { query : queryModified })

          if (!isPolling) {
            // make dummy request to get the page count of commentary
            const commentary = getPlayByPlay({ edition, params, location: locationModified })

            commentary.promise
              .then((res) => {
                const pageCount = selectn('data.commentary.pageCount', res)
                commentaryArgs.page = pageCount
                dispatchPlayByPlay(commentaryArgs)
              })
          } else {
            dispatchPlayByPlay(commentaryArgs)
          }
        })
        .catch((e) => {
          reject(e)
          console.error(e)
        })
    })
  }
}

export function getPlayByPlay({ edition = {}, params = {}, location = {}, page, isPolling, appProps }) {
  const { query = {} } = location
  const host = getHost('site','', appProps)
  const { innings, filter } = query
  let qsObj = {
    event: 951333,
    contentorigin: 'espn'
  }

  if (params.gameId) {
    qsObj.event = params.gameId
  }

  if ((/^superover$/ig).test(innings) === true) {
    qsObj.sort = 'overs:asc'
    qsObj.tiebreaker = true
  } else {
    // Check if commentary needs to be fetched for a particular innings
    if (innings) {
      let period = parseInt(innings, 10) || 1
      if ([ 1,2,3,4 ].indexOf(period) > -1) {
        qsObj.period = period
      }
    }

    if (filter && filter.toUpperCase() !== 'FULL') {
      qsObj.playtype = filter
    }

    qsObj.page = page
  }

  const url = `${params.origin || host}/apis/site/v2/sports/${params.sport}/${params.league}/playbyplay${getQueryString(qsObj, edition, params, true)}`
  return {
    url,
    edition,
    params,
    location,
    isPolling,
    type: actionTypes.GET_PLAY_BY_PLAY,
    promise: memoizr.get(url),
    transform: transform.onGetPlayByPlay
  }
}

/**
 * get the commentary in next page
 * @param  {Object} edition     edition config
 * @param  {Object} params      router params
 * @param  {Object} location    router location
 * @param  {Number} page        next page number to be loaded
 * @return {Function}           handled by thunk middleware
 */
export function getPlayByPlayMore({ edition = {}, params = {}, location = {}, page, isPolling, appProps }) {
  /*
   * Thunk middleware calls this function
   * with dispatch and getState method pointers of Store
   */
  return (dispatch, getState) => {
    return new Promise((resolve) => {
      dispatchPlayByPlay({ dispatch, state : getState(), edition, params, location, page, resolve, isPolling, appProps })
    })
  }
}

export function dispatchPlayByPlay({ dispatch, state = {}, edition = {}, params = {}, location = {}, page, resolve, isPolling = false, appProps }) {
  const { gamePackage = {} } = state
  const { selInnings = {}, selCmtryFilter = {}, fullCommentary = {} } = gamePackage
  const { query = {} } = location
  const { pageCount } = fullCommentary

  // no commentary polling if selected innings is not current innings
  if (isPolling === true && selInnings.isCurrent === false) {
    dispatch({ type : null })
  }
  const innings = query.innings || selInnings.period || ''
  const filter = query.filter || selCmtryFilter.playtype || ''

  const queryModified = Object.assign({}, query, {
    innings,
    filter
  })

  const locationModified = Object.assign({}, location, { query : queryModified })

  // poll the latest page
  if (isPolling === true) {
    page = pageCount

    if(fullCommentary.commSwitch === true) {
      page = 1
    }
  }

  // do nothing if no page number available
  if (!page) {
    dispatch({ type : null })
  }

  const commentary = getPlayByPlay({ edition, params, location: locationModified, page, isPolling, appProps })
  commentary.promise
    .then((commentary_res) => {

      dispatch(apiReqComplete())

      dispatch({
        edition,
        params,
        url: commentary.url,
        location: locationModified,
        isPolling,
        data: commentary.transform({ res: commentary_res, edition, params, location: locationModified }),
        type: commentary.type
      })

      resolve(true)
    })
    .catch((e) => {
      dispatch(apiReqComplete())
      // Since we dispatch apiReqComplete above, assuming we try to gracefully handle errors and let the page load?
      // Hence resolve instead of reject.
      // This prevents invalid URLs like /cricket/series/0/report/0 from holding up the request and going forver.
      resolve(true)
      console.error(e)
    })
}

/**
 * An Example of calling multiple APIs
 * in series having dependencies
 * @param  {Object} edition     edition config
 * @param  {Object} params      Params
 * @param  {Object} location    router location
 * @return {Function}           Handled by thunk middleware
 */
export function getStandings({ edition = {}, params = {}, location = {}, appProps }) {
  return (dispatch, getState) => {
    return new Promise((resolve) => {

      const leaguesAction = getLeagues({ edition, params, appProps })
      const { promise: leaguesPromise, url: leaguesUrl } = leaguesAction

      leaguesPromise
        .then(res => {
          const leagues = selectn('data.leagues', res) || []
          let defaultLeagueId

          for(let league of leagues) {
            if (league.hasStandings === true) {
              defaultLeagueId = league.id
              break
            }
          }

          // TODO: error check
          return {
            data: res.data,
            id: params.league || defaultLeagueId
          }
        })
        .then(res => {
          let routeParams = Object.assign({},params,{
            league : res.id
          })

          dispatch({
            edition,
            location,
            params: routeParams,
            url: leaguesUrl,
            data : transform.onGetLeagues(res, edition, routeParams, location),
            type: actionTypes.GET_LEAGUES
          })

          routeParams.slug = selectn('leagues.currentLeague.slug', getState())

          const standingsAction = getStandingsCommon({ edition, routeParams })
          const { promise: standingsPromise, url: standingsUrl } = standingsAction

          standingsPromise
            .then( (res) => {

              dispatch({
                edition,
                location,
                params: routeParams,
                url: standingsUrl,
                data : transform.onGetStandings({ res, edition, routeParams, location }),
                type: actionTypes.GET_STANDINGS
              })
              // Resolve promise once the standings api has finished loading
              resolve(true)
            })
            .catch(() => {
              dispatch( getErrorRedirect(params) )
            })
        })
    })
  }
}

export default {
  getPlayByPlay,
  getPlayByPlayMore,
  getCommentaryTab,
  getStandings
}



// WEBPACK FOOTER //
// ../app/actions/api/cricket.js